import { Writable, Transform, PassThrough } from 'stream'
import MapStream from 'pw-map-stream'
import GraphData from 'pw-client-graph-data'
import AlertGraphType from './AlertGraphType'
import _ from 'lodash'

var graphs = {}
var groups = {}
const graphOptionsCache = {}

var PassThroughGraph = () => {
  const graph = PassThrough({ objectMode: true })
  graph.destroy = graph.end
  return graph
}


var wrapData = (type, graphId) => (graphData) => ({ type, graphId, graphData })
var graphConstructors = {
  "graph/counter": GraphData.counter,
  "graph/multiCounter": GraphData.multiCounter,
  "graph/summaryCounter": GraphData.counter,
  "graph/latestCounter": GraphData.latestCounter,
  "graph/connection": GraphData.connection,
  "profilerProtocol": GraphData.profilerProtocol,
  "profilerIp": GraphData.profilerIp,
  "graph/asset": PassThroughGraph,
  "graph/asset-sensors": PassThroughGraph,
  "entity/list-entries": PassThroughGraph,
  "entity/base-data": PassThroughGraph,
  "entity/threat-data": PassThroughGraph,
  "graph/observation": () => new AlertGraphType(),
  "graph/event": () => new AlertGraphType(),
}

var createGroupMessage = function (action, message) {
  var dataOptions = message.dataOptions
  var sensorId
  if (dataOptions.sensorId === null || typeof dataOptions.sensorId === 'number') {
    sensorId = dataOptions.sensorId
  } else if (typeof dataOptions.sensorId === 'string') {
    sensorId = Number(dataOptions.sensorId)
  } else if (Array.isArray(dataOptions.agentIds) && dataOptions.agentIds.length > 0) {
    sensorId = Number(dataOptions.agentIds[0])
  }

  var newOptions = {
    endTime: dataOptions.endTime,
    windowSize: Number(dataOptions.windowSize) || 0
  }
  if (sensorId || sensorId === null) {
    newOptions.sensorId = sensorId
  }

  var newMessage = {
    action: action,
    path: 'graph/group',
    name: message.groupName,
    options: newOptions
  }
  return Promise.resolve(newMessage)
}

var transformGraphOptions = function (message) {
  var dataOptions = message.dataOptions

  var stackBy
  if (dataOptions.aggregate === 'stack') {
    stackBy = 'type'
  } else if (dataOptions.stackBy) {
    stackBy = dataOptions.stackBy
  } else if (message.type === 'subscribe') {
    stackBy = 'none'
  }

  var aggregate
  if (dataOptions.aggregate && dataOptions.aggregate !== 'stack') {
    aggregate = dataOptions.aggregate
  }

  var minWindowSize = Math.ceil(dataOptions.minWindowSize)
  var numPoints = Math.ceil(dataOptions.numPoints)

  var size
  if (minWindowSize || message.type === 'subscribe') {
    if (!minWindowSize || minWindowSize < 45000) {
      size = "small"
    } else if (minWindowSize < 90000) {
      size = "medium"
    } else if (minWindowSize < (7 * 60 * 1000)) {
      size = "large"
    } else {
      size = "macro"
    }
  }

  var options = {}

  if (dataOptions.hasOwnProperty('entries')) {
    options.entries = dataOptions.entries
  }
  if (dataOptions.hasOwnProperty('year')) {
    options.year = dataOptions.year
  }
  if (dataOptions.hasOwnProperty('month')) {
    options.month = dataOptions.month
  }
  if (dataOptions.hasOwnProperty('version')) {
    options.version = dataOptions.version
  }
  if (dataOptions.hasOwnProperty('endTime')) {
    options.endTime = dataOptions.endTime
  }
  if (dataOptions.hasOwnProperty('timeWindow')) {
    options.timeWindow = dataOptions.timeWindow
  }

  if (message.hasOwnProperty('groupName')) {
    options.group = message.groupName
  }
  if (dataOptions.hasOwnProperty('sensorIds')) {
    options.sensorIds = dataOptions.sensorIds
  }
  if (dataOptions.hasOwnProperty('counterTypes')) {
    options.counterTypes = dataOptions.counterTypes
  }
  if (dataOptions.hasOwnProperty('summaryType')) {
    options.summaryType = dataOptions.summaryType
  }
  if (dataOptions.hasOwnProperty('observedDuration')) {
    options.observedDuration = dataOptions.observedDuration
  }
  if (dataOptions.hasOwnProperty('retroHistoryDuration')) {
    options.retroHistoryDuration = dataOptions.retroHistoryDuration
  }
  if (stackBy) {
    options.stackBy = stackBy
  }
  if (aggregate) {
    options.aggregate = aggregate
  }
  if (size) {
    options.size = size
  }
  if (numPoints) {
    options.numPoints = numPoints
  }
  return options
}

var graphTransforms = {
  "graph/counter": function (data, enc, next) {
    _.each(data, this.push.bind(this))
    next()
  },
  "graph/profilerProtocol": function (data, enc, next) {
    _.each(data, this.push.bind(this))
    next()
  },
  "graph/multiCounter": function (data, enc, next) {
    _.each(data, this.push.bind(this))
    next()
  },
  "graph/latestCounter": function (data, enc, next) {
    next(null, data)
  },
  "graph/summaryCounter": function (data, enc, next) {
    _.each(data, this.push.bind(this))
    next()
  },
  "graph/connection": function (data, enc, next) {
    next(null, data)
  },
  "graph/asset": function (data, enc, next) {
    next(null, data)
  },
  "graph/asset-sensors": function (data, enc, next) {
    next(null, data)
  },
  "entity/list-entries": function (data, enc, next) {
    next(null, data)
  },
  "entity/base-data": function (data, enc, next) {
    next(null, data)
  },
  "entity/threat-data": function (data, enc, next) {
    next(null, data)
  },
  "graph/observation": function (data, enc, next) {
    next(null, data)
  },
  "graph/event": function (data, enc, next) {
    next(null, data)
  }
}

const entityGraphs = ['list-entries', 'base-data', 'threat-data']
const createPath = function (graphType) {
  const prefix = entityGraphs.indexOf(graphType) > -1 ? 'entity' : 'graph'
  return prefix + "/" + graphType
}

var transforms = {
  createGroup: _.partial(createGroupMessage, 'create'),
  groupOptions: _.partial(createGroupMessage, 'update'),
  destroyGroup: function (message) {
    var newMessage = {
      action: 'delete',
      path: 'graph/group',
      name: message.groupName
    }
    return Promise.resolve(newMessage)
  },
  unsubscribe: function (message) {
    var newMessage = {
      action: 'delete',
      path: 'graph/graph',
      name: message.graphId
    }
    return Promise.resolve(newMessage)
  },
  subscribe: function (message) {
    var graphType = message.dataOptions.type
    if (graphType === 'connectionRollup') { graphType = 'connection' }

    graphOptionsCache[message.graphId] = message.dataOptions // Copy options for easier usage in subscriber listener later

    var newMessage = {
      action: 'create',
      path: createPath(graphType),
      name: message.graphId,
      options: transformGraphOptions(message),
      viewOptions: message.viewOptions
    }
    return Promise.resolve(newMessage)
  },
  dataRequest: function (message) {
    return Promise.resolve({
      action: 'read',
      name: message.graphId
    })
  },
  graphDataOptions: function (message) {
    var graphType = message.dataOptions.type
    if (graphType === 'connectionRollup') { graphType = 'connection' }

    var newMessage = {
      action: 'update',
      path: createPath(graphType),
      name: message.graphId,
      options: transformGraphOptions(message)
    }
    return Promise.resolve(newMessage)
  },

  // graphViewOptions() changes trigger a re-transformation of graphData that
  // is already present in the graph instance, it does not emit any messages to
  // the server. Use for transforms that do not require newly queried data.
  graphViewOptions: function (message) {
    var newMessage = {
      action: 'viewOptions',
      name: message.graphId,
      viewOptions: message.viewOptions
    }
    return Promise.resolve(newMessage)
  }
}

class SubscriberTransform extends Transform {
  constructor() {
    super({ objectMode: true })
  }

  _transform(data, enc, next) {
    var transform = transforms[data.type]
    if (!transform) {
      console.warn("could not transform: ", data)
      return next(null)
    }

    transform(data).then(function (transformedData) {
      next(null, transformedData)
    })
  }
}

var consoleSink = Writable({ objectMode: true })
consoleSink._write = function (data, enc, next) {
  // console.log("Data: ", data)
  next()
}


class StreamingClientAdapter {
  constructor(options) {
    var self = this
    this.streamingClient = options.streamingClient
    this.workerToUiGraphBus = options.workerToUiGraphBus
    this.uiToWorkerGraphBus = options.uiToWorkerGraphBus
    this.workerToUiGraphSink = Writable({
      objectMode: true, write: (data, enc, next) => {
        this.workerToUiGraphBus.push(data)
        next()
      }
    })

    var streamingClient = this.streamingClient

    var subscriberTransform = new SubscriberTransform()
    this.uiToWorkerGraphBus.onValue(subscriberTransform.write.bind(subscriberTransform))
    subscriberTransform.on('data', function (message) {
      var wrapDataUpdate = wrapData('dataUpdate', message.name)
      var stream = null
      var dataUpdate = null
      switch (message.action) {
        case 'create':
          stream = streamingClient.create(message)
          stream.on('destroyed', function () {
            if (message.path === 'graph/group') {
              if (!groups[message.name]) { return }
              _.each(groups[message.name].subGraphs, function (nextGraphName) {
                var nextStream = streamingClient.getDataStream(nextGraphName)
                if (!nextStream) { return }
                nextStream.destroy({ sendMessage: false })
              })
              delete groups[message.name]
              return
            }
            if (!graphs[message.name]) { return }
            graphs[message.name].destroy()
            delete graphs[message.name]
          })

          // Allow UI-only viewType constructors to be used by specifying a type in viewOptions
          var _constructorName = _.get(message, 'viewOptions.viewType') || message.path
          var graphConstructor = graphConstructors[_constructorName]

          if (message.path === 'graph/group') {
            groups[message.name] = { subGraphs: [] }
          } else if (graphConstructor) {
            graphs[message.name] = graphConstructor(message.options, message.viewOptions)

            if (groups[message.options.group]) {
              groups[message.options.group].subGraphs.push(message.name)
            } else {
              console.warn("GROUP DOESN'T EXIST: ", message.options.group, message.name)
            }

            var counterDataTransform = Transform({ objectMode: true })
            counterDataTransform._transform = graphTransforms[message.path]

            stream.pipe(counterDataTransform).pipe(graphs[message.name])
            if (graphs[message.name].readable) {
              graphs[message.name].
                pipe(MapStream(wrapDataUpdate)).
                pipe(self.workerToUiGraphSink, { end: false })
            }
            stream.on('reset-history', function (eventData) {
              if (!graphs[message.name]) { return }
              graphs[message.name].resetHistory(eventData)
            })

            stream.on('data-loaded', function () {
              if (!graphs[message.name]) { return }
              dataUpdate = wrapDataUpdate(graphs[message.name].getGraphData() || {})
              graphs[message.name].dataLoaded = true
              self.workerToUiGraphBus.push(dataUpdate)
            })

            stream.on('options-updated', function (updatedOptions) {
              graphs[message.name].setOptions(updatedOptions)
            })

          } else {
            console.warn("no constructor for graph: ", message)
          }
          break

        case 'read':
          stream = streamingClient.getDataStream(message.name)
          if (!stream || !graphs[message.name]) {
            console.warn("Can't read stream: ", message.name)
            return
          }
          if (!graphs[message.name].dataLoaded && !graphs[message.name].ignoreDataLoaded) {
            return
          }
          dataUpdate = wrapDataUpdate(graphs[message.name].getGraphData() || {})
          self.workerToUiGraphBus.push(dataUpdate)
          stream.readData()
          break

        case 'update':
          stream = streamingClient.getDataStream(message.name)
          if (!stream) { return }
          stream.updateOptions(message.options)
          break

        case 'delete':
          stream = streamingClient.getDataStream(message.name)
          if (!stream) {
            // console.log("could not delete stream: ", message.name)
            return
          }
          stream.destroy()
          break

        case 'viewOptions':
          if (!graphs[message.name] || !graphs[message.name].setViewOptions) {
            console.warn(`Graph "${message.name}": no graph or setViewOptions method available`)
            return
          }
          // Update graph's view options
          graphs[message.name].setViewOptions(message.viewOptions)
          // Request fresh data based on the new view options
          dataUpdate = {
            type: "dataUpdate",
            graphId: message.name,
            graphData: graphs[message.name].getGraphData() || {}
          }
          self.workerToUiGraphBus.push(dataUpdate)
          break

        default:
          console.warn("Unsupported Action:", message)
          break
      }
    })

  }
}

export default StreamingClientAdapter
